import { LooseObjectType } from './globalTypes';

export const PROMISE_STATUS = {
  pending: 'pending',
  fulfilled: 'fulfilled',
  rejected: 'rejected',
};

export const API_REQUEST_METHODS = {
  get: { name: 'GET_API', method: 'get' },
  post: { name: 'POST_API', method: 'post' },
  put: { name: 'PUT_API', method: 'put' },
  patch: { name: 'PATCH_API', method: 'patch' },
  delete: { name: 'DELETE_API', method: 'delete' },
};

export const STORAGE_KEYS = {
  persistRoot: 'persist:root',
  inactivity: 'inactivity',
};

export const PANGO_BILLING_AUTHORIZATION_TOKEN = `Basic ${import.meta.env.VITE_PANGO_BILLING_AUTH_TOKEN}`;
export const PANGO_REDEEM_AUTHORIZATION_TOKEN = `Basic ${import.meta.env.VITE_PANGO_REDEEM_AUTH_TOKEN}`;

export const API_ENDPOINTS = {
  baseURL: import.meta.env.VITE_BASE_URL,
  pangoBaseURL: import.meta.env.VITE_PANGO_BASE_URL,
  pwmgrURL: import.meta.env.VITE_PWMGR_URL,
  usAddressUrl: import.meta.env.VITE_US_ADDRESS_URL,
  breachCodes: 'breachcodes',
  v1Enrollment: 'api/client/enrollments/thin',
  v2Enrollment: 'api/client/enrollments',
  flexEnrollment: 'api/client/enrollments/flex/user',
  flexEnrollmentPayment: 'api/client/enrollments/flex/payment',
  v1ErrollmentInformation: 'api/client/enrollments/info',
  userSession: 'api/client/sessions',
  refreshToken: 'api/client/sessions/refresh',
  darkWebWatchlist: 'api/client/monitored_items',
  statesOfUS: 'api/ref/countries/us/states_provinces',
  alerts: '/api/client/alerts',
  userProfile: 'api/client/users',
  passwordScan: 'password-exposure',
  getMarketingOffer: '/api/client/marketing/offer',
  getMarketingOfferDetails: '/api/ref/marketing/offers',
  forgotPassword: '/api/client/unverified_users/forgot_password?username=',
  validateToken: '/api/client/unverified_users/validate?token=',
  verifyToken: '/api/client/unverified_users/verify?token=',
  resetPassword: '/api/client/unverified_users/{token}/reset_password',
  dbooUser: 'dboo/user',
  notifications: 'api/client/notifications',
  getBrandDetails: 'brands',
  enrollmentInfo: '/api/client/enrollments/info?token=',
  planDetails: '/api/client/plans/',
  accountProfile: 'api/client/users/account',
  updateDevice: '/api/client/recipients/channels/sms/devices/undefined',
  createDevice: '/api/client/recipients/channels/sms/devices',
  preferences: '/api/client/recipients/preferences',
  companyCode: '/api/ref/companies',
  termsOfService: '/cms/',
  /* Secure document upload */
  secureUploadSettings: '/userfile/client/setting',
  secureUploadUrl: '/userfile/client/upload-url',
  secureUserFileClient: '/userfile/client',
  /* Security - 2FA */
  sessionSendSecurityOTP: '/api/client/sessions/2fa/send',
  sessionVerifySecurityOTP: '/api/client/sessions/2fa/verify',
  getSecurityChannels: '/api/client/users/2fa',
  addSecurityChannel: '/api/client/users/2fa/add',
  removeSecurityChannel: '/api/client/users/2fa/remove',
  addChannelVerifySecurityOTP: '/api/client/users/2fa/verify',
  accountSendSecurityOTP: '/api/client/users/account/2fa/send',
  accountVerifySecurityOTP: '/api/client/users/account/2fa/verify',
  /* Convert To Pay */
  zuoraRSATokens: '/pango-billing/zuora/rsa-signatures',
  getAvailableOffers: '/api/client/marketing/offers/available',
  getSubscriptionInfo: '/api/client/subscribers/',
  eligibility: 'api/client/users/eligibility',
  conversionEligibility: 'api/client/users/conversion_eligibility',
  getSubscriptions: '/api/client/subscriptions',
  createZuoraAccount: '/pango-billing/account/create',
  acceptLegalPolicies: '/api/client/accepted_legal_policies',
  updateZuoraAccount: '/pango-billing/account/update',
  changeMarketingOffer: '/api/client/marketing/offers/change',
  getPangoBillingOfferDetails: '/pango-billing/offer/get',
  findPlan: '/pango-billing/plan/find',
  redeemSession: 'auth/session/redeem',
  getBillingAccountInfo: 'pango-billing/account/info',
  updateBillingAccountInfo: 'pango-billing/account/update',
  /* Collect marketing email opt-in */
  emailOptInEventTracking: 'event-tracking/user/web',
  userEventTracking: 'event-tracking/user',
  /* Home Title Monitoring */
  htmonProperties: 'hometitle-monitoring/address',
  creditScore: 'credit_scores',
  creditScoreTU: '/api/client/credit_scores',
};

export const WITH_BEARER_TOKEN = new Set([
  API_ENDPOINTS.refreshToken,
  API_ENDPOINTS.userSession,
  API_ENDPOINTS.secureUploadSettings,
]);

export const WITH_OUT_BEARER_TOKEN = new Set([
  API_ENDPOINTS.alerts,
  API_ENDPOINTS.breachCodes,
  API_ENDPOINTS.darkWebWatchlist,
  API_ENDPOINTS.passwordScan,
  API_ENDPOINTS.dbooUser,
  API_ENDPOINTS.userProfile,
  API_ENDPOINTS.notifications,
  API_ENDPOINTS.planDetails,
  API_ENDPOINTS.accountProfile,
  API_ENDPOINTS.preferences,
  API_ENDPOINTS.updateDevice,
  API_ENDPOINTS.createDevice,
  API_ENDPOINTS.getMarketingOffer,
  API_ENDPOINTS.termsOfService,
  API_ENDPOINTS.secureUploadUrl,
  API_ENDPOINTS.secureUserFileClient,
  API_ENDPOINTS.getSubscriptions,
  API_ENDPOINTS.getSecurityChannels,
  API_ENDPOINTS.addSecurityChannel,
  API_ENDPOINTS.removeSecurityChannel,
  API_ENDPOINTS.addChannelVerifySecurityOTP,
  API_ENDPOINTS.accountSendSecurityOTP,
  API_ENDPOINTS.accountVerifySecurityOTP,
  API_ENDPOINTS.getAvailableOffers,
  API_ENDPOINTS.acceptLegalPolicies,
  API_ENDPOINTS.changeMarketingOffer,
  API_ENDPOINTS.getSubscriptionInfo,
  API_ENDPOINTS.htmonProperties,
  API_ENDPOINTS.creditScore,
  API_ENDPOINTS.creditScoreTU,
  API_ENDPOINTS.userEventTracking,
]);

export const GENERAL = {
  mobileSize: 700,
  tabletSize: 940,
  refreshTokenInterval: 300000, // 5 mins
  noOneCanPreventAllIdentityTheft: 'No one can prevent all identity theft.',
  termsOfUse: 'Terms of use',
  polices: 'Policies',
  faqs: 'FAQs',
  alertsPerPage: 10,
  phoneNumberMask: '(000) 000-0000',
  zipMask: '00000',
  active: 'active',
  inactive: 'inactive',
  blocked: 'blocked',
  defaultErrorMsg:
    'Something went wrong. Please try again or contact the Customer Care Center.',
};

export const OS = {
  mac: 'mac',
  windows: 'windows',
  ios: 'ios',
  android: 'android',
  other: 'other',
};

export const BROWSER = {
  chrome: 'Chrome',
  msEdge: 'Edge',
  firefox: 'Firefox',
  safari: 'Safari',
  other: 'Other',
};

export const OS_BROWSER_SUPPORT_MESSAGES = {
  browserNotSupport:
    'Password Defense is only available for Chrome, Safari, Edge, and Firefox.',
  osNotSupport:
    'Password Defense is only available for devices running an Apple or Android operating system.',
  vpnInTouchNotSupport:
    'VPN In Touch is only available for iOS, Android, Windows and Mac.',
};

export const VPN_IN_TOUCH_DOWNLOAD_LINKS = {
  [OS.windows]:
    'https://firebasestorage.googleapis.com/v0/b/vpn-in-touch-windows/o/Installer%2FVPNInTouch.exe?alt=media&token=d7c84dcd-c219-4690-bfd1-f6695c2c9687',
  [OS.android]:
    'https://play.google.com/store/apps/details?id=com.vpnintouch.android&hl=en&gl=US',
  [OS.ios]: 'https://apps.apple.com/us/app/vpn-in-touch/id464241430',
  [OS.mac]:
    'https://firebasestorage.googleapis.com/v0/b/web-portal-for-partners.appspot.com/o/products%2FVPN%20in%20Touch%201.6.0.dmg?alt=media&token=c25f4831-9279-40f9-a650-fffedd2e29f2',
};

export const PASSWORD_DEFENSE_INSTALL_LINKS = {
  [OS.android]:
    'https://play.google.com/store/apps/details?id=co.pango.passworddefense',
  [OS.ios]: 'https://apps.apple.com/app/password-defense/id6503000237',
};

export const PASSWORD_DEFENSE_EXTENSION_LINKS = {
  [BROWSER.chrome]:
    'https://chromewebstore.google.com/detail/password-defense/jldcelchacngobkjhgboanjfdmolihha',
  [BROWSER.msEdge]:
    'https://microsoftedge.microsoft.com/addons/detail/password-defense/bjamgiegaombbhmmomjfgcfblelcapol',
  [BROWSER.firefox]:
    'https://addons.mozilla.org/en-US/firefox/addon/password-defense/',
  [BROWSER.safari]:
    'https://apps.apple.com/us/app/password-defense-extension/id6505006082?mt=12',
};

export const DEEP_LINK_APPS = {
  vpnInTouch: 'vpnInTouch',
  passwordDefense: 'passwordDefense',
};

export const DEEP_LINKS = {
  [DEEP_LINK_APPS.vpnInTouch]: 'https://www.pango.co/vpn-in-touch/',
  [DEEP_LINK_APPS.passwordDefense]: 'https://www.pango.co/password-defense/',
};

export const WINDOWS_DEEP_LINKS = {
  [DEEP_LINK_APPS.vpnInTouch]: 'vpnintouch://',
  [DEEP_LINK_APPS.passwordDefense]: 'passworddefense://',
};

export const LINKS = {
  cyex: 'http://www.cyex.com/',
  cyexScan: 'https://scan.cyex.com/',
  duckDuckGo: 'https://duckduckgo.com',
  duckDuckGoTermsOfUse: 'https://duckduckgo.com/terms',
  duckDuckGoPrivacyPolicy: 'https://duckduckgo.com/privacy',
  passwordScanTermsOfService: 'https://haveibeenpwned.com/TermsOfUse',
  passwordScanPrivacyPolicy: 'https://haveibeenpwned.com/Privacy',
};

export const REG_EX = {
  lettersSpacesHyphensApostrophes: /^[a-zA-Z\u00C0-\u02AF\s'-]{1,}$/,
  lettersSpacesPeriodsHyphensApostrophes:
    /^(?!\s)(?!.*\s$)[a-zA-Z0-9\s\-.'#]{1,}$/,
  lettersNumbersSpacesHyphensSlashes: /^[a-zA-Z0-9\s\-/]*$/,
  lettersNumbersSpacesPeriodsHyphensSlashesHashes: /^[a-zA-Z0-9\s\-./#]{1,}$/,
  lettersNumbersSpacesHyphensPeriods: /^(?!\s)(?!.*\s$)[a-zA-Z0-9\s\-.]{1,}$/,
  lettersNumbersSpacesHyphensUnderscore:
    /^(?!\s)(?!.*\s$)[a-zA-Z0-9\s\-._]{1,}$/,
  lettersSpacesAsterisksHyphens: /^(?!\s)(?!.*\s$)[a-zA-Z0-9*\s-]{1,}$/,
  lettersSpacesUnderscoresHyphensPeriods: /^[a-zA-Z0-9\s_.-]{1,}$/,
  lettersNumbersUnderscoreHyphenPeriods: /^[a-zA-Z0-9_.-]{1,}$/,
  lettersNumbersPeriodUnderscore: /^[a-zA-Z0-9_.]+$/,
  lettersNumbersPeriod: /^[a-zA-Z0-9.]+$/,
  lettersNumbersUnderscore: /^[a-zA-Z0-9_]+$/,
  lettersNumbers: /^[a-z0-9]*$/i,
  lettersNumbersSpaces: /^[a-z0-9\s]*$/i,
  lettersSpacesHyphen: /^[a-z\s-]*$/i,
  lettersSpaces: /^[a-z\s]*$/i,
  startsWithLetterNumberHyphenUnderscore: /^[a-zA-Z][a-zA-Z0-9\-_]*$/,
  numbers: /^[\d]{0,}$/,
  phoneNumber: /^[2-9][0-9]*$/,
  noSpace: /^\S*$/,
  websiteDomain:
    /^((https?|ftp):\/\/)?(([a-z\d]([a-z\d-]*[a-z\d])?\.)+[a-z]{2,}|localhost)(\/[-a-z\d%_.~+]*)*(\?[;&a-z\d%_.~+=-]*)?(#[-a-z\d_]*)?$/i,
  networkIP:
    /\b(?:\d{1,3}\.){3}\d{1,3}\b|(?:[0-9a-fA-F]{1,4}:){7}[0-9a-fA-F]{1,4}\b/,
  email:
    /^\s*[-a-z0-9~!$%^&*_=+}{\\'?]+(\.[-a-z0-9~!$%^&*_=+}{\\'?]+)*@([a-z0-9_][-a-z0-9_]*(\.[-a-z0-9_]+)*\.([a-z]{2,})|([0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}))(:[0-9]{1,5})?\s*$/i,
  domainFromUrl: /^(?:https?:\/\/)?(?:[^@/\n]+@)?(?:www\.)?([^:/?\n]+)/gim,
  startsWithoutPlusMinusEqualtoAttherate: /^(?![+=\-@])[\s\S]*$/,
  lettersNumbersSpacesHyphensSlashesHashesPeriods: /^[a-zA-Z0-9\s\-./#]*$/,
  ssn: /^(?!666|000|9\d{2})(?!\d{3}00)(?!\d{5}0000)\d{9}$|^(9\d{2})([-]?)(5\d|6[0-5]|7\d|8[0-8]|9[0-2]|9[4-9])([-]?)(\d{4})$/,
};

export const DARK_WEB_WATCH_LIST: LooseObjectType = {
  initialShowCount: 3,
  lockItems: ['name', 'birthDate', 'ssn'],
  acceptedCardTypes: {
    visa: 'visa',
    mastercard: 'mastercard',
    'american-express': 'american express',
    discover: 'discover',
  },
};

export const DATA_BROKER_OPT_OUT = {
  inProgress: 'in_progress',
  scheduledDateFormat: 'MMM DD',
  optOutDateFormat: 'MMM DD, YYYY',
};

export const DIGITAL_VAULT = {
  previewFileTypes: ['jpeg', 'jpg', 'png'],
  addedDateFormat: 'MMM DD, YYYY',
  fallbackDocumentType: 'livingWill',
};

export const SUFFIX_OPTIONS = [
  {
    value: '',
    label: 'None',
  },
  {
    value: 'JR',
    label: 'JR',
  },
  {
    value: 'SR',
    label: 'SR',
  },
  {
    value: 'I',
    label: 'I',
  },
  {
    value: 'II',
    label: 'II',
  },
  {
    value: 'III',
    label: 'III',
  },
  {
    value: 'IV',
    label: 'IV',
  },
  {
    value: 'V',
    label: 'V',
  },
  {
    value: 'VI',
    label: 'VI',
  },
  {
    value: 'VII',
    label: 'VII',
  },
  {
    value: 'VIII',
    label: 'VIII',
  },
];

export const PHONE_NUMBER_TYPE_OPTIONS = [
  {
    value: 'mobile',
    label: 'Mobile',
  },
  {
    value: 'work',
    label: 'Work',
  },
  {
    value: 'home',
    label: 'Home',
  },
  {
    value: 'main',
    label: 'Main',
  },
  {
    value: 'fax',
    label: 'Fax',
  },
  {
    value: 'pager',
    label: 'Pager',
  },
  {
    value: 'other',
    label: 'Other',
  },
];

export const VISA_NUMBER_TYPE_OPTIONS = [
  {
    value: 'green_card_number',
    label: 'Green Card Number',
  },
  {
    value: 'visa_number',
    label: 'Visa Number',
  },
];

export const ACCOUNT_TYPE_OPTIONS = [
  {
    value: 'checking',
    label: 'Checking',
  },
  {
    value: 'savings',
    label: 'Savings',
  },
  {
    value: 'retirement',
    label: 'Retirement',
  },
  {
    value: 'investment',
    label: 'Investment',
  },
];

export const PASSWORD_STRENGTH_INDICATOR: LooseObjectType = {
  Bad: {
    label: 'Bad',
    tooltip: 'This password is insecure and puts your account at risk',
  },
  Weak: {
    label: 'Weak',
    tooltip:
      'This password lacks complexity and uniqueness. It may be easily guessed or be susceptible to dictionary attacks.',
  },
  Good: {
    label: 'Good',
    tooltip:
      'This password offers moderate protection for your account. It could be better.',
  },
  Strong: {
    label: 'Strong',
    tooltip:
      'This password offers better than average protection. It is strong enough to make it difficult to crack.',
  },
  Excellent: {
    label: 'Excellent',
    tooltip: 'This password provides the highest level of security',
  },
};

export const ENROLLMENT_ERROR_MESSAGE: LooseObjectType = {
  offerBrandMismatch:
    'We encountered an issue. The offer you’re enrolling with is not valid. Contact support if you need assistance.',
};

export const ENV: 'test' | 'stage' | 'prod' =
  import.meta.env.VITE_PS_ENV || 'test';
export const smartyEmbeddedKey = import.meta.env.VITE_SMARTY_API_KEY;
export const smartyAddressUrl = import.meta.env.VITE_US_ADDRESS_URL;

/* Security 2FA */
export const SECURITY_CHANNEL_TYPES = {
  email: 'email',
  sms: 'sms',
  call: 'call',
} as const;
export const SECURITY_ACTION_TYPES = {
  session: 'session',
  account: 'account',
  add: 'add',
} as const;

/* Privacy global plan code */
export const PRIVACY_GLOBAL_PLAN_CODE = 'psglobind';

export const CTP_ELGIBILITY_DETAILS = {
  subscriber: 'subscriber',
  pendingCancelation: 'pending cancellation',
  pendingCtp: 'BREACH_COMPLIMENTARY_LIMITED_OFFER_CHANGE_SCHEDULED',
  ctp: 'ctp',
  orderSummary: 'orderSummary',
};

/* EyeOn support pages */
export const EYEON_SUPPORT_PAGES = [
  {
    title: 'Miss Hosting SE/NO',
    href: 'https://misshosting.help',
  },
  {
    title: 'Domene',
    href: 'https://www.domene.no/kontakt',
  },
  {
    title: 'Hemsida24',
    href: 'https://hemsida24.help/sv',
  },
];

/* Plan entitlements */
export const PLAN_ENTITLEMENT_KEYS = {
  customerNotificationMethods: 'itps:plan_customer_notification_methods',
  customerCareSupportMethods: 'itps:plan_customer_care_support_methods',
};

// cancel membership reasons
export const CANCEL_MEMBERSHIP_REASONS = {
  TOO_EXPENSIVE: 'TOO_EXPENSIVE',
  NO_VALUE_OR_NEED: 'NO_VALUE_OR_NEED',
  BETTER_ALTERNATIVE: 'BETTER_ALTERNATIVE',
  NO_LONGER_NEEDED: 'NO_LONGER_NEEDED',
  TECHNICAL_ISSUES: 'TECHNICAL_ISSUES',
  OTHERS: 'OTHERS',
} as const;

/* Finish account setup popup/banner display waiting period in days */
export const finishAccountSetupWaitingPeriod = 90;

export const CANCEL_MEMBERSHIP_REASONS_OPTIONS = [
  {
    label: 'The cost of the service is too high',
    value: CANCEL_MEMBERSHIP_REASONS.TOO_EXPENSIVE,
  },
  {
    label: 'I didn’t find the service helpful',
    value: CANCEL_MEMBERSHIP_REASONS.NO_VALUE_OR_NEED,
  },
  {
    label: 'I found a better or more affordable alternative',
    value: CANCEL_MEMBERSHIP_REASONS.BETTER_ALTERNATIVE,
  },
  {
    label: 'I no longer need privacy protection',
    value: CANCEL_MEMBERSHIP_REASONS.NO_LONGER_NEEDED,
  },
  {
    label: 'I’ve been experiencing technical issues',
    value: CANCEL_MEMBERSHIP_REASONS.TECHNICAL_ISSUES,
  },
  {
    label: 'Other',
    value: CANCEL_MEMBERSHIP_REASONS.OTHERS,
  },
];

export const OFFER_CHANGING = 'OFFER_CHANGING';

/* Credit score features */
export const CREDIT_BUREAUS = {
  experian: 'experian',
  transunion: 'transunion',
  equifax: 'equifax',
} as const;

export const CREDIT_SCORE_STATUS = {
  unknown: 'unknown',
  thinFile: 'thin_file',
  unavailable: 'credit_score_unavailable',
  success: 'success',
  calculated: 'calculated',
} as const;

/* User Events */
export const USER_EVENTS = {
  viewCtpOptInNotification: 'ICON_CTP_CLICK',
  dismissCtpOptInNotification: 'ICON_CTP_DISMISS',
  viewFinishSetupBanner: 'CC_BANNER_CLICKED',
  viewCtpBanner: 'CTP_BANNER_CLICKED',
};
/* End of user events */
